interface BookingFormSkeletonProps {
  /** 'smartflow' = borderless single-column (logo, one question, input, button);
   * 'classic' = the bordered two-column form+calendar card. */
  variant?: 'smartflow' | 'classic';
  /** When true (modal / full-page), fill the viewport with logo pinned top and a
   * powered-by row at the bottom; otherwise render a compact centred block. */
  fullHeight?: boolean;
  /** Omit the logo placeholder (e.g. when the page renders its own logo header). */
  hideLogo?: boolean;
}

/**
 * Loading skeleton that matches the booking form it stands in for, so the form
 * doesn't visibly swap layouts when it mounts. Kept byte-identical to the
 * dashboard copy at src/components/booking/BookingFormSkeleton.tsx.
 */
export function BookingFormSkeleton({ variant = 'smartflow', fullHeight = false, hideLogo = false }: BookingFormSkeletonProps) {
  if (variant === 'classic') {
    return (
      <div className="w-full">
        <div className="mx-auto max-w-4xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col lg:h-[500px] lg:flex-row">
            <div className="flex-1 p-6 lg:border-r lg:border-gray-200">
              <div className="mb-4 h-6 w-32 animate-pulse rounded bg-gray-200" />
              <div className="mb-6 h-4 w-48 animate-pulse rounded bg-gray-100" />
              <div className="space-y-4">
                <div className="h-10 animate-pulse rounded bg-gray-100" />
                <div className="h-10 animate-pulse rounded bg-gray-100" />
                <div className="h-10 animate-pulse rounded bg-gray-100" />
              </div>
            </div>
            <div className="hidden flex-1 bg-gray-50 p-6 lg:block">
              <div className="mb-4 h-5 w-24 animate-pulse rounded bg-gray-200" />
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 35 }).map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded bg-gray-100" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const inner = (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div className="h-4 w-20 animate-pulse rounded bg-gray-100" />
      <div className="h-8 w-2/3 animate-pulse rounded bg-gray-200" />
      <div className="h-12 w-full animate-pulse rounded bg-gray-100" />
      <div className="h-12 w-44 animate-pulse rounded bg-gray-200" />
    </div>
  );

  if (fullHeight) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        {!hideLogo && (
          <div className="flex shrink-0 justify-center px-4 pb-4 pt-8">
            <div className="h-9 w-40 animate-pulse rounded bg-gray-200" />
          </div>
        )}
        <div className="flex min-h-0 flex-1 flex-col justify-center px-4">{inner}</div>
        <div className="flex shrink-0 justify-center border-t border-gray-100 py-4">
          <div className="h-5 w-28 animate-pulse rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8">
      {!hideLogo && (
        <div className="mb-6 flex justify-center">
          <div className="h-9 w-40 animate-pulse rounded bg-gray-200" />
        </div>
      )}
      {inner}
    </div>
  );
}
